import { useEffect, useState } from "react";
import { useTranslation, type Locale } from "./i18n";
import { printTestPage } from "./print";
import {
  api,
  type Invitation,
  type Me,
  type Printer,
  type RestaurantMember,
} from "./api";

const LANGUAGES: { locale: Locale; flag: string; label: string }[] = [
  { locale: "en", flag: "🇬🇧", label: "English" },
  { locale: "pl", flag: "🇵🇱", label: "Polski" },
  { locale: "ru", flag: "🇷🇺", label: "Русский" },
];

const BRANDS = ["All", "Epson", "Star", "Bixolon", "Citizen", "Custom"];

const ROLE_RANK: Record<string, number> = {
  viewer: 10,
  staff: 20,
  manager: 30,
  owner: 40,
};

const ROLE_LABELS: Record<string, string> = {
  viewer: "Viewer",
  staff: "Staff",
  manager: "Manager",
  owner: "Owner",
};

function buildInviteLink(token: string): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  url.searchParams.set("invite", token);
  return url.toString();
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    window.prompt("Copy this link", text);
    return false;
  }
}

type Props = {
  me: Me;
  onToast: (t: { title: string; body: string; type: "success" | "error" | "info" }) => void;
  printers: Printer[];
  onPrintersChange: (printers: Printer[]) => void;
};

export function SettingsPage({ me, onToast, printers, onPrintersChange }: Props) {
  const { locale, setLocale, t } = useTranslation();
  const restaurantId = me.memberships[0]?.restaurant_id;
  const [newIp, setNewIp] = useState("");
  const [newName, setNewName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState<any[]>([]);
  const [brandFilter, setBrandFilter] = useState("All");
  const [subnet, setSubnet] = useState("192.168.1");

  const [members, setMembers] = useState<RestaurantMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState<{ email: string; link: string } | null>(null);

  const myRole = me.memberships.find((m) => m.restaurant_id === restaurantId)?.role ?? "";
  const myRank = me.is_superuser ? ROLE_RANK.owner : (ROLE_RANK[myRole] ?? 0);
  const canManageTeam = myRank >= ROLE_RANK.manager;
  const allowedRoles = Object.keys(ROLE_RANK)
    .filter((role) => ROLE_RANK[role] <= myRank)
    .sort((a, b) => ROLE_RANK[a] - ROLE_RANK[b]);
  const effectiveRole = allowedRoles.includes(inviteRole)
    ? inviteRole
    : (allowedRoles[0] ?? "staff");

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.listMembers(restaurantId);
        if (!cancelled) setMembers(list);
      } catch (err: any) {
        if (!cancelled) onToast({ title: "Error", body: err.message, type: "error" });
      }
      if (!canManageTeam) return;
      try {
        const pending = await api.listInvitations(restaurantId);
        if (!cancelled) setInvitations(pending);
      } catch {
        // Managers always pass the guard; a failure here is not worth a toast.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurantId, canManageTeam, onToast]);

  const handleLanguageChange = async (newLocale: Locale) => {
    try {
      await api.updateMe({ preferred_language: newLocale });
      setLocale(newLocale);
      onToast({ title: t("toast.settings_saved"), body: t("settings.saved"), type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const addPrinter = async () => {
    if (!newIp.trim() || !restaurantId) return;
    try {
      const p = await api.createPrinter(restaurantId, {
        ip: newIp.trim(),
        name: newName.trim() || `Printer ${printers.length + 1}`,
        port: 9100,
        is_active: true,
      });
      onPrintersChange([...printers, p]);
      setNewIp("");
      setNewName("");
      onToast({ title: t("settings.printer"), body: "Printer added", type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const togglePrinterActive = async (printerId: string, current: boolean) => {
    try {
      const updated = await api.updatePrinter(printerId, { is_active: !current });
      onPrintersChange(printers.map(p => p.id === printerId ? updated : p));
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const toggleAutoPrint = async (printerId: string, field: string, current: boolean) => {
    try {
      const updated = await api.updatePrinter(printerId, { [field]: !current });
      onPrintersChange(printers.map(p => p.id === printerId ? updated : p));
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const removePrinter = async (printerId: string) => {
    try {
      await api.deletePrinter(printerId);
      onPrintersChange(printers.filter(p => p.id !== printerId));
      onToast({ title: t("settings.printer"), body: "Printer removed", type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const inviteMember = async () => {
    if (!restaurantId || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      const created = await api.createInvitation(
        restaurantId,
        inviteEmail.trim(),
        effectiveRole,
      );
      const link = buildInviteLink(created.token);
      setLastInvite({ email: created.email, link });
      setInviteEmail("");
      setInvitations(await api.listInvitations(restaurantId));
      onToast({ title: t("settings.team"), body: t("settings.team_invited"), type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async (link: string) => {
    const copied = await copyToClipboard(link);
    if (copied) {
      onToast({ title: t("settings.team"), body: t("settings.team_link_copied"), type: "info" });
    }
  };

  const revokeInvitation = async (invitation: Invitation) => {
    if (!restaurantId) return;
    try {
      await api.revokeInvitation(restaurantId, invitation.id);
      setInvitations((prev) => prev.filter((i) => i.id !== invitation.id));
      onToast({ title: t("settings.team"), body: t("settings.team_revoked"), type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const removeMember = async (member: RestaurantMember) => {
    if (!restaurantId) return;
    if (!window.confirm(t("settings.team_remove_confirm", { email: member.email }))) return;
    try {
      await api.removeMember(restaurantId, member.user_id);
      setMembers((prev) => prev.filter((m) => m.user_id !== member.user_id));
      onToast({ title: t("settings.team"), body: t("settings.team_removed"), type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const scanNetwork = async () => {    if (!restaurantId) return;
    setScanning(true);
    setScanResults([]);
    try {
      const res = await api.discoverPrinters(restaurantId, subnet);
      let found = res.printers || [];
      if (brandFilter !== "All") {
        found = found.filter((p: any) => p.brand_hint && p.brand_hint.toLowerCase().includes(brandFilter.toLowerCase()));
      }
      setScanResults(found);
      if (found.length === 0) {
        onToast({ title: t("settings.printer"), body: "No printers found on network", type: "info" });
      } else {
        onToast({ title: t("settings.printer"), body: `Found ${found.length} printer(s)`, type: "success" });
      }
    } catch (err: any) {
      onToast({ title: "Error", body: err.message || "Scan failed", type: "error" });
    } finally {
      setScanning(false);
    }
  };

  const addFromScan = async (printer: any) => {
    if (!restaurantId) return;
    try {
      const p = await api.createPrinter(restaurantId, {
        ip: printer.ip,
        name: printer.brand_hint || `Printer`,
        port: printer.port || 9100,
        brand: printer.brand_hint || "",
        is_active: true,
      });
      onPrintersChange([...printers, p]);
      onToast({ title: t("settings.printer"), body: `Added ${printer.ip}`, type: "success" });
    } catch (err: any) {
      onToast({ title: "Error", body: err.message, type: "error" });
    }
  };

  const sendTestPrint = async (ip: string, port: number) => {
    if (!restaurantId) return;
    try {
      const res = await api.testPrinter(restaurantId, ip, port);
      if (res.status === "ok") {
        onToast({ title: t("settings.test_print"), body: `Sent to ${ip}`, type: "success" });
      } else {
        onToast({ title: "Error", body: res.message || "Test print failed", type: "error" });
      }
    } catch {
      // Fallback to browser print
      printTestPage();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Language */}
      <div className="card">
        <h2>{t("settings.language")}</h2>
        <p className="muted" style={{ marginBottom: 20 }}>{t("settings.language_desc")}</p>
        <div className="lang-grid">
          {LANGUAGES.map(({ locale: loc, flag, label }) => (
            <div
              key={loc}
              className={`lang-card ${locale === loc ? "lang-active" : ""}`}
              onClick={() => handleLanguageChange(loc)}
            >
              <span className="emoji-flag">{flag}</span>
              <div style={{ fontWeight: 600, marginTop: 8 }}>{label}</div>
              {locale === loc && (
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--accent-primary)" }}>● Selected</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Team */}
      <div className="card">
        <h2>{t("settings.team")}</h2>
        <p className="muted" style={{ marginBottom: 20 }}>{t("settings.team_desc")}</p>

        <h3 className="section-label">{t("settings.team_members")}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {members.map((member) => (
            <div
              key={member.user_id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #ffffff11",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {member.full_name || member.email}
                  {member.user_id === me.id && (
                    <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                      ({t("settings.team_you")})
                    </span>
                  )}
                </div>
                {member.full_name && (
                  <div className="muted" style={{ fontSize: 12 }}>{member.email}</div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="platform-badge">{ROLE_LABELS[member.role] ?? member.role}</span>
                {canManageTeam && member.user_id !== me.id && (
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "6px 12px", color: "var(--status-cancelled)" }}
                    onClick={() => removeMember(member)}
                  >
                    {t("settings.team_remove")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {canManageTeam ? (
          <>
            <h3 className="section-label" style={{ marginTop: 24 }}>{t("settings.team_invite")}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, alignItems: "end" }}>
              <div>
                <label className="label" htmlFor="inviteEmail">{t("settings.team_invite_email")}</label>
                <input
                  id="inviteEmail"
                  className="input"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="inviteRole">{t("settings.team_invite_role")}</label>
                <select
                  id="inviteRole"
                  className="input"
                  value={effectiveRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {allowedRoles.map((role) => (
                    <option key={role} value={role}>{ROLE_LABELS[role] ?? role}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={inviteMember}
                disabled={inviting || !inviteEmail.trim()}
              >
                {inviting ? t("settings.team_inviting") : t("settings.team_invite_send")}
              </button>
            </div>

            {lastInvite && (
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.3)",
                  background: "rgba(99,102,241,0.08)",
                }}
              >
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  {t("settings.team_link_ready", { email: lastInvite.email })}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="input" readOnly value={lastInvite.link} onFocus={(e) => e.target.select()} />
                  <button className="btn btn-secondary" onClick={() => copyInvite(lastInvite.link)}>
                    {t("settings.team_copy")}
                  </button>
                </div>
              </div>
            )}

            <h3 className="section-label" style={{ marginTop: 24 }}>{t("settings.team_pending")}</h3>
            {invitations.filter((i) => !i.accepted_at).length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>{t("settings.team_no_pending")}</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {invitations
                  .filter((i) => !i.accepted_at)
                  .map((invitation) => (
                    <div
                      key={invitation.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: "1px solid #ffffff11",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {invitation.email}
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {ROLE_LABELS[invitation.role] ?? invitation.role} ·{" "}
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: "6px 12px", color: "var(--status-cancelled)" }}
                        onClick={() => revokeInvitation(invitation)}
                      >
                        {t("settings.team_revoke")}
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </>
        ) : (
          <p className="muted" style={{ marginTop: 16, fontSize: 13 }}>
            {t("settings.team_only_managers")}
          </p>
        )}
      </div>

      {/* Printer Management */}
      <div className="card">
        <h2>{t("settings.printer")}</h2>
        <p className="muted" style={{ marginBottom: 20 }}>{t("settings.printer_desc")}</p>

        {/* Saved Printers */}
        {printers.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h3 className="section-label">Active Printers</h3>
            <div className="printer-list">
              {printers.map((p) => (
                <div key={p.id} className={`printer-card ${p.is_active ? "printer-active" : ""}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div
                        className="printer-radio"
                        onClick={() => togglePrinterActive(p.id, p.is_active)}
                      >
                        {p.is_active && <div className="printer-radio-dot" />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {p.ip}:{p.port} {p.brand && `• ${p.brand}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: "6px 12px" }}
                        onClick={() => sendTestPrint(p.ip, p.port)}
                      >
                        🖨️ Test
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: "6px 12px", color: "var(--status-cancelled)" }}
                        onClick={() => removePrinter(p.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="toggle-list" style={{ borderTop: "1px solid #ffffff11", paddingTop: 12 }}>
                    <label className="toggle-row" style={{ padding: "4px 0" }}>
                      <span style={{ fontSize: 12 }}>{t("settings.auto_new")}</span>
                      <input
                        type="checkbox"
                        className="toggle-switch"
                        checked={p.auto_print_new}
                        onChange={() => toggleAutoPrint(p.id, "auto_print_new", p.auto_print_new)}
                      />
                    </label>
                    <label className="toggle-row" style={{ padding: "4px 0" }}>
                      <span style={{ fontSize: 12 }}>{t("settings.auto_accept")}</span>
                      <input
                        type="checkbox"
                        className="toggle-switch"
                        checked={p.auto_print_accepted}
                        onChange={() => toggleAutoPrint(p.id, "auto_print_accepted", p.auto_print_accepted)}
                      />
                    </label>
                    <label className="toggle-row" style={{ padding: "4px 0" }}>
                      <span style={{ fontSize: 12 }}>{t("settings.auto_reject")}</span>
                      <input
                        type="checkbox"
                        className="toggle-switch"
                        checked={p.auto_print_rejected}
                        onChange={() => toggleAutoPrint(p.id, "auto_print_rejected", p.auto_print_rejected)}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Printer Manually */}
        <h3 className="section-label">Add Printer</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div>
            <label className="label">{t("settings.printer_ip")}</label>
            <input
              className="input"
              placeholder="192.168.1.100"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("settings.printer_name")}</label>
            <input
              className="input"
              placeholder="Kitchen Printer"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={addPrinter}>
            + Add
          </button>
        </div>

        {/* Network Scanner */}
        <div style={{ marginTop: 24 }}>
          <h3 className="section-label">Search Network</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "end" }}>
            <div>
              <label className="label">Subnet</label>
              <input
                className="input"
                placeholder="192.168.1"
                value={subnet}
                onChange={(e) => setSubnet(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Brand</label>
              <select className="input" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
                {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              onClick={scanNetwork}
              disabled={scanning}
              style={{ minWidth: 130 }}
            >
              {scanning ? "⏳ Scanning..." : "🔍 Search"}
            </button>
          </div>
          {scanResults.length > 0 && (
            <div className="scan-results" style={{ marginTop: 16 }}>
              {scanResults.map((p: any, i: number) => (
                <div key={i} className="scan-result-row">
                  <div>
                    <strong>{p.ip}</strong>:{p.port}
                    {p.brand_hint && <span className="muted" style={{ marginLeft: 8 }}>({p.brand_hint})</span>}
                  </div>
                  <button className="btn btn-primary" style={{ fontSize: 12, padding: "6px 16px" }} onClick={() => addFromScan(p)}>
                    + Add
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="muted" style={{ fontSize: 12, textAlign: "center" }}>
        Changes are saved automatically to your account.
      </div>
    </div>
  );
}
